import { useEffect, useReducer } from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import {
  BubbleChartRenderingLayer,
  BubbleChartRenderingLayerProps,
  BubbleChartViewModelSourceData,
} from '@ardoq/bubble-chart';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import {
  buildViewpointModeViewModelSourceData,
  getInitialViewpointModeSourceDataState,
  warmConditionalFormattingFilterCache,
  type BuildViewpointModeBubbleChartSourceDataArgs,
  ViewpointModeBubbleChartSourceDataArgs,
} from './sourceData/buildViewpointModeViewModelSourceData';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import { getBubbleChartViewComponentIds } from './viewComponentIds';
import type { ViewpointModeBubbleChartSettingsControlsDependencies } from './viewpointMode/settingsControls';

type RenderingLayerProps = Omit<
  BubbleChartRenderingLayerProps<ViewpointModeBubbleChartSettingsControlsDependencies>,
  'sourceData'
>;

export type ViewpointModeBubbleChartSourceDataLayerProps = RenderingLayerProps &
  ViewpointModeBubbleChartSourceDataArgs &
  HasDataWorkToken & {
    viewStatusReporter: ViewStatusReporter;
  };

type SourceDataState = {
  sourceData: BubbleChartViewModelSourceData;
  dataWorkToken: string | null;
};

const initSourceDataState = (viewInstanceId: string): SourceDataState => ({
  sourceData: getInitialViewpointModeSourceDataState(viewInstanceId),
  dataWorkToken: null,
});

const updateSourceDataState = (
  { sourceData }: SourceDataState,
  {
    dataWorkToken,
    ...sourceDataArgs
  }: BuildViewpointModeBubbleChartSourceDataArgs & HasDataWorkToken
): SourceDataState => ({
  sourceData: buildViewpointModeViewModelSourceData(sourceData, sourceDataArgs),
  dataWorkToken,
});

const publishEmptyStateData = ({
  emptyStateData,
}: BubbleChartViewModelSourceData) =>
  dispatchAction(
    updateViewEmptyStateData({
      viewEmptyStateData: emptyStateData,
      viewId: ViewIds.BUBBLE,
    })
  );

const publishViewComponentIds = ({ data }: BubbleChartViewModelSourceData) =>
  dispatchAction(
    readViewComponentIds({
      [ViewIds.BUBBLE]: getBubbleChartViewComponentIds(data),
    })
  );

const ViewpointModeBubbleChartSourceDataLayer = ({
  viewInstanceId,
  viewSettingsForRenderingData,
  bubbleChartCommands,
  settingsControlsHandlers,
  onRenderingDataChange,
  BubbleChartComponent,
  ViewLegendComponent,
  EmptyStateComponent,
  ZoomControlsComponent,
  trackingCallbacks,
  context,
  viewSettings,
  loadedGraph,
  scopeDiffState,
  activeDiffMode,
  permissionContext,
  locale,
  featureContext,
  i18nLocale,
  orgUsersById,
  formattingAndLabelRules,
  referenceLabelFilters,
  trackingId,
  dataWorkToken,
  viewStatusReporter,
}: ViewpointModeBubbleChartSourceDataLayerProps) => {
  const [
    { sourceData, dataWorkToken: renderedDataWorkToken },
    updateSourceData,
  ] = useReducer(updateSourceDataState, viewInstanceId, initSourceDataState);

  useEffect(() => {
    const filterColorData = warmConditionalFormattingFilterCache(loadedGraph);
    updateSourceData({
      context,
      viewSettings,
      loadedGraph,
      scopeDiffState,
      activeDiffMode,
      permissionContext,
      locale,
      featureContext,
      i18nLocale,
      orgUsersById,
      formattingAndLabelRules,
      referenceLabelFilters,
      filterColorData,
      trackingId,
      dataWorkToken,
    });
  }, [
    context,
    viewSettings,
    loadedGraph,
    scopeDiffState,
    activeDiffMode,
    permissionContext,
    locale,
    featureContext,
    i18nLocale,
    orgUsersById,
    formattingAndLabelRules,
    referenceLabelFilters,
    trackingId,
    dataWorkToken,
  ]);

  useEffect(() => {
    publishEmptyStateData(sourceData);
    publishViewComponentIds(sourceData);
  }, [sourceData]);

  useEffect(() => {
    if (renderedDataWorkToken) {
      viewStatusReporter.reportWorkFinished(renderedDataWorkToken);
    }
  }, [viewStatusReporter, renderedDataWorkToken]);

  return (
    <BubbleChartRenderingLayer<ViewpointModeBubbleChartSettingsControlsDependencies>
      viewInstanceId={viewInstanceId}
      sourceData={sourceData}
      viewSettingsForRenderingData={viewSettingsForRenderingData}
      bubbleChartCommands={bubbleChartCommands}
      settingsControlsHandlers={settingsControlsHandlers}
      onRenderingDataChange={onRenderingDataChange}
      BubbleChartComponent={BubbleChartComponent}
      ViewLegendComponent={ViewLegendComponent}
      EmptyStateComponent={EmptyStateComponent}
      ZoomControlsComponent={ZoomControlsComponent}
      trackingCallbacks={trackingCallbacks}
    />
  );
};

export { ViewpointModeBubbleChartSourceDataLayer };
